// Purchase flow API service for interacting with backend
import axios from 'axios';
import { cookieManager } from '../utils/cookieManager';
import { getBaseApiUrl, getCorsSettings } from '../utils/apiConfig';
import { handle401Error } from '../utils/handle401Error';
import { HEADER_KEYS, STORAGE_KEYS } from '../constants/identity';
import { getAfgloReferral } from '../utils/afglo'; // AFFILIATE TRACKING

// Base API configuration
const API_BASE_URL = getBaseApiUrl();
const corsSettings = getCorsSettings();

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  ...corsSettings,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for authentication
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add user ID header from authenticated user or localstorage
    const userId = localStorage.getItem(STORAGE_KEYS.USER_ID);
    if (userId) {
      config.headers[HEADER_KEYS.USER_ID] = userId;
    }
    
    // Add UUID from cookie for user recognition
    const userUuid = cookieManager.getUserUuid();
    if (userUuid) {
      config.headers[HEADER_KEYS.USER_UUID] = userUuid;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 authentication errors
    if (error.response?.status === 401) {
      handle401Error();
      return Promise.reject(error);
    }

    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

export const purchaseService = {
  // Subscription Management
  async previewSubscription(planId, billingAddress, promotionalCode = null) {
    try {
      const response = await api.post('/subscriptions/preview', {
        plan_id: planId,
        billing_address: billingAddress,
        promotional_code: promotionalCode
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error?.message || 'Failed to preview subscription');
    }
  },

  async createSubscription(planId, paymentMethodId, billingAddress, overageSettings, promotionalCode = null) {
    try {
      const response = await api.post('/subscriptions/create', {
        plan_id: planId,
        payment_method_id: paymentMethodId,
        billing_address: billingAddress,
        overage_settings: overageSettings,
        promotional_code: promotionalCode
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error?.message || 'Failed to create subscription');
    }
  },

  // Create subscription with tax information (following Stripe Tax guidelines)
  async createSubscriptionWithTax(subscriptionData) {
    try {
      //!!!!!const response = await api.post('/subscriptions/create-with-tax', subscriptionData);
      const response = await api.post('/subscriptions/create', subscriptionData);
      return response.data;
    } catch (error) {
      // Fallback to regular subscription creation if the with-tax endpoint doesn't exist
      console.warn('Falling back to regular subscription creation:', error.message);
      return await this.createSubscription(
        subscriptionData.plan_id,
        subscriptionData.payment_method_id,
        subscriptionData.customer_details?.address,
        subscriptionData.overage_settings,
        subscriptionData.promotional_code
      );
    }
  },

  async updateSubscriptionPlan(subscriptionId, newPlanId, prorationBehavior = 'create_prorations') {
    try {
      const response = await api.put(`/subscriptions/${subscriptionId}/update-plan`, {
        new_plan_id: newPlanId,
        proration_behavior: prorationBehavior
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error?.message || 'Failed to update subscription plan');
    }
  },

  // Credit Purchase (Pay as You Go)
  async purchaseCredits(amount, paymentMethodId, billingAddress, isAutoReload = false) {
    try {
      const response = await api.post('/credits/purchase', {
        amount: amount,
        payment_method_id: paymentMethodId,
        billing_address: billingAddress,
        is_auto_reload: isAutoReload
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error?.message || 'Failed to purchase credits');
    }
  },

  // Payment Intent Creation
  async createPaymentIntent(paymentData) {
    try {
      // Map our payment data to the backend's expected format
      const requestData = {
        paymentMethodType: 'card', // Default to card, could be made dynamic
        currency: 'usd',
        amount: paymentData.amount, // Should already be in cents
        subscription_initial_payment: paymentData.subscription_initial_payment || false,
        ...(paymentData.force_setup_intent && { force_setup_intent: true }),
        // Add customer email and name if provided
        email: paymentData.email,
        name: paymentData.name,
        plan_type: paymentData.planType,
        afglo_referral: getAfgloReferral(), // AFFILIATE TRACKING
      };
      
      const response = await api.post('/payment', requestData);
      
      return response.data;
    } catch (error) {
      console.error('API error:', error);
      console.error('Error response:', error.response?.data);
      throw new Error(error.response?.data?.message || 'Failed to create payment intent');
    }
  },


  async updatePaymentIntentAmount(paymentIntentId, amount, planType) {
    try {
      const response = await api.patch('/payment', {
        paymentIntentId,
        amount,
        plan_type: planType
      });
      return response.data;
    } catch (error) {
      console.error('Failed to update PaymentIntent amount:', error);
      throw error;
    }
  },

  // Payment Success Processing (Pre-Authentication)
  async storePaymentData(paymentIntentId, planSelection, isSetupIntent = false) {
    try {
      const response = await api.post('/payment/store-payment', {
        payment_intent_id: paymentIntentId,
        plan_selection: planSelection,
        is_setup_intent: isSetupIntent
      });
      return response.data;
    } catch (error) {
      console.error('Payment storage error:', error);
      console.error('Error response:', error.response?.data);
      throw new Error(error.response?.data?.error || 'Failed to store payment data');
    }
  },

  // Payment Success Processing (Post-Authentication)
  async processPaymentSuccess(paymentIntentId, planSelection) {
    try {
      const response = await api.post('/payment/process-success', {
        payment_intent_id: paymentIntentId,
        plan_selection: planSelection
      });
      return response.data;
    } catch (error) {
      console.error('Payment processing error:', error);
      console.error('Error response:', error.response?.data);
      throw new Error(error.response?.data?.error || 'Failed to process payment success');
    }
  },

  // Payment Methods
  async getPaymentMethods(userId) {
    try {
      const response = await api.get('/payment-methods', {
        params: { user_id: userId }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error?.message || error.response?.data?.error || 'Failed to get payment methods');
    }
  },

  async addPaymentMethod(paymentMethodId, setAsDefault = false) {
    try {
      const response = await api.post('/payment-methods/add', {
        payment_method_id: paymentMethodId,
        set_as_default: setAsDefault
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error?.message || 'Failed to add payment method');
    }
  },

  async deletePaymentMethod(paymentMethodId) {
    try {
      const response = await api.delete(`/payment-methods/${paymentMethodId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error?.message || 'Failed to delete payment method');
    }
  },

  // User Settings
  async getOverageSettings(userId) {
    try {
      const response = await api.get(`/users/${userId}/overage-settings`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error?.message || 'Failed to get overage settings');
    }
  },

  async updateOverageSettings(userId, settings) {
    try {
      const response = await api.put(`/users/${userId}/overage-settings`, settings);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error?.message || 'Failed to update overage settings');
    }
  },

  async getAutoReloadSettings(userId) {
    try {
      const response = await api.get(`/users/${userId}/auto-reload`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error?.message || 'Failed to get auto-reload settings');
    }
  },

  async updateAutoReloadSettings(userId, settings) {
    try {
      const response = await api.put(`/users/${userId}/auto-reload`, settings);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error?.message || 'Failed to update auto-reload settings');
    }
  },

  // Usage and Billing
  async getCurrentUsage(userId) {
    try {
      const response = await api.get(`/users/${userId}/usage/current`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error?.message || 'Failed to get current usage');
    }
  },

  async getBillingHistory(userId, limit = 10, offset = 0) {
    try {
      const response = await api.get(`/users/${userId}/billing/history`, {
        params: { limit, offset }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error?.message || 'Failed to get billing history');
    }
  },

  // Subscription Tax Estimation
  async estimateSubscriptionTax(planId, customerIp = null, billingAddress = null) {
    try {
      // Build request data, only including non-null values
      const requestData = {
        plan_id: planId
      };
      
      if (customerIp !== null && customerIp !== undefined) {
        requestData.customer_ip = customerIp;
      }
      
      if (billingAddress !== null && billingAddress !== undefined) {
        requestData.billing_address = billingAddress;
      }
      
      console.log('Tax estimation request data:', requestData);
      
      const response = await api.post('/subscriptions/tax-estimate', requestData);
      return response.data;
    } catch (error) {
      console.error('Tax estimation failed:', error);
      console.error('Request data was:', {
        plan_id: planId,
        customer_ip: customerIp,
        billing_address: billingAddress
      });
      console.error('Error response data:', error.response?.data);
      
      // Development fallback: Return mock tax estimation data
      if (import.meta.env.VITE_ENVIRONMENT === 'development') {
        console.warn('Using mock tax estimation data due to API error');
        const planPrice = planId === '6e5164c6-d8de-4cbf-a6d7-ad420781482a' ? 29.99 : 19.99;
        const mockTax = planPrice * 0.08;
        
        return {
          plan: {
            id: planId,
            name: planId === '6e5164c6-d8de-4cbf-a6d7-ad420781482a' ? 'Premium' : 'Solo',
            price: planPrice.toString(),
            currency: 'USD',
            plan_type: planId === '6e5164c6-d8de-4cbf-a6d7-ad420781482a' ? 'premium' : 'solo'
          },
          tax_estimation: {
            is_ip_based: true,
            estimated_location: { country: 'US', state: 'CA' },
            amount_before_tax: planPrice,
            estimated_tax: mockTax,
            estimated_total: planPrice + mockTax,
            currency: 'USD',
            tax_breakdown: [],
            is_tax_exempt: false,
            disclaimer: 'Mock tax estimate for development. Final tax will be calculated when you provide your billing address.'
          },
          billing_preview: {
            billing_cycle: {
              start: new Date().toISOString(),
              end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
              frequency: 'monthly'
            },
            next_billing_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
            proration: false
          }
        };
      }
      
      throw new Error(error.response?.data?.error?.message || error.response?.data?.message || 'Failed to estimate tax');
    }
  },

  async calculateSubscriptionTax(planId, customerDetails, promotionalCode = null, validateAddress = true) {
    try {
      const requestData = {
        plan_id: planId,
        customer_details: customerDetails,
        validate_address: validateAddress
      };
      
      // Only include promotional_code if it's not null/undefined
      if (promotionalCode) {
        requestData.promotional_code = promotionalCode;
      }
      
      console.log('Tax calculation request data:', JSON.stringify(requestData, null, 2));
      
      const response = await api.post('/subscriptions/tax-calculate', requestData);
      return response.data;
    } catch (error) {
      console.error('Tax calculation failed:', error);
      console.error('Response data:', error.response?.data);
      throw new Error(error.response?.data?.error?.message || 'Failed to calculate tax');
    }
  },

  // IP-based location estimation for tax
  async getClientLocationForTax() {
    try {
      // Use a simple IP geolocation service for tax estimation
      // In production, you might want to use a more reliable service
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();

      return {
        country: data.country_code || 'US',
        state: data.region_code || 'CA',
        city: data.city || 'Unknown'
      };
    } catch (error) {
      console.error('Failed to get client location:', error);
      // Fallback to default US location
      return {
        country: 'US',
        state: 'CA',
        city: 'San Francisco'
      };
    }
  },

  // Cloudflare-based country detection (Primary method for homepage)
  async detectHomeCountry() {
    // Check cache first (valid for 24 hours)
    const cacheKey = 'home_country_cache';
    const cached = localStorage.getItem(cacheKey);

    if (cached) {
      try {
        const { country, timestamp } = JSON.parse(cached);
        const age = Date.now() - timestamp;

        // Cache valid for 24 hours
        if (age < 24 * 60 * 60 * 1000) {
          console.log('✅ Using cached country:', country);
          return country;
        }
      } catch (e) {
        console.error('Cache parse error:', e);
        // Invalid cache, continue to detection
      }
    }

    try {
      // Call backend API that uses Cloudflare headers (primary) and ipapi.co (fallback)
      const response = await api.get('/user/location');
      const { country, source } = response.data;

      console.log(`✅ Country detected: ${country} (source: ${source})`);

      // Cache the result
      localStorage.setItem(cacheKey, JSON.stringify({
        country,
        source,
        timestamp: Date.now()
      }));

      return country;

    } catch (error) {
      console.error('❌ Backend country detection failed, using client-side fallback:', error);

      // Fallback to client-side ipapi.co
      try {
        const location = await this.getClientLocationForTax();
        return location.country;
      } catch (fallbackError) {
        console.error('❌ All detection methods failed:', fallbackError);
        return 'US'; // Ultimate fallback
      }
    }
  }
};

// Helper functions for common operations
export const purchaseHelpers = {
  // Calculate tax for a given address
  calculateTax(subtotal, address) {
    // Basic US tax calculation (in production, use Stripe Tax API)
    if (address.country === 'US') {
      const stateTaxRates = {
        'WA': 0.10,
        'CA': 0.08,
        'NY': 0.08,
        'TX': 0.06,
        'FL': 0.06,
      };
      const taxRate = stateTaxRates[address.state] || 0.05;
      return subtotal * taxRate;
    }
    return 0;
  },

  // Format currency
  formatCurrency(amount, currency = 'USD') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  },

  // Get plan details by type
  getPlanDetails(planType) {
    const plans = {
      premium: {
        id: 'premium-plan-id',
        name: 'Premium',
        price: 29.99,
        billing_type: 'subscription',
        device_limit: 6, // Updated to match database
        data_allocation_mb: 30720, // 30GB
        features: ['6 devices', 'Data rollover', 'Flex billing', '10GB bonus'], // Updated feature text
        overage_enabled: true,
        default_overage_limit: 100.00
      },
      solo: {
        id: 'solo-plan-id',
        name: 'Solo',
        price: 19.99,
        billing_type: 'subscription',
        device_limit: 2, // Updated to match database
        data_allocation_mb: 15360, // 15GB
        features: ['2 devices', 'Data rollover', 'Flex billing', '5GB bonus'], // Updated feature text
        overage_enabled: true,
        default_overage_limit: 100.00
      },
      paygo: {
        id: 'paygo-plan-id',
        name: 'Pay as You Go',
        price: 0,
        billing_type: 'credit',
        device_limit: 4, // Updated to match database
        data_allocation_mb: null,
        features: ['4 devices', 'Credit-based', 'Auto-reload', '5GB bonus'], // Updated feature text
        overage_enabled: false,
        default_overage_limit: 0
      }
    };

    return plans[planType] || null;
  },

  // Validate billing address
  validateBillingAddress(address) {
    const errors = [];
    
    if (!address.line1) errors.push('Address line 1 is required');
    if (!address.city) errors.push('City is required');
    if (!address.postal_code) errors.push('Postal code is required');
    if (!address.country) errors.push('Country is required');
    
    if (address.country === 'US' && !address.state) {
      errors.push('State is required for US addresses');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  },

  // Store purchase flow state
  savePurchaseState(state) {
    localStorage.setItem('purchase_flow_state', JSON.stringify({
      ...state,
      timestamp: Date.now()
    }));
  },

  // Retrieve purchase flow state
  getPurchaseState() {
    try {
      const stored = localStorage.getItem('purchase_flow_state');
      if (!stored) return null;
      
      const state = JSON.parse(stored);
      
      // Check if state is less than 1 hour old
      const oneHour = 60 * 60 * 1000;
      if (Date.now() - state.timestamp > oneHour) {
        localStorage.removeItem('purchase_flow_state');
        return null;
      }
      
      return state;
    } catch (error) {
      console.error('Error retrieving purchase state:', error);
      localStorage.removeItem('purchase_flow_state');
      return null;
    }
  },

  // Calculate tax for Pay as You Go credit purchases via API
  async calculatePayGoTax(amount, billingAddress, userId = '00000000-0000-0000-0000-000000000000') {
    try {
      // Use the api instance from the top of this file
      const response = await api.post('/tax/calculate', {
        user_id: userId,
        amount: amount, // Keep in dollars for tax calculation
        currency: 'USD',
        billing_address: billingAddress,
        tax_inclusive: false,
        calculation_type: 'credit_purchase',
        line_items: [{
          amount: amount, // Keep in dollars for tax calculation
          description: `Pay-as-You-Go Credit Purchase - $${amount}`,
          metadata: {
            credit_amount: amount.toString(),
            plan_type: 'pay_as_you_go'
          }
        }]
      });
      
      if (response.data.success) {
        console.log('PayGo tax calculation successful:', response.data.data);
        // Backend now returns amounts in dollars, no need to convert
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Tax calculation failed');
      }
    } catch (error) {
      console.error('PayGo tax calculation error:', error);
      console.error('Request data:', { amount, billingAddress, userId });
      
      // Fallback to helper function if API fails
      console.warn('Using fallback tax calculation');
      const fallbackTax = this.calculateTax(amount, billingAddress);
      
      return {
        amount_before_tax: amount,
        tax_amount: fallbackTax,
        total_amount: amount + fallbackTax,
        currency: 'USD',
        tax_breakdown: [],
        is_tax_exempt: false,
        fallback: true
      };
    }
  },

  // Clear purchase flow state
  clearPurchaseState() {
    localStorage.removeItem('purchase_flow_state');
  }
};

export default purchaseService;
